import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfWeek, startOfMonth, endOfDay } from 'date-fns';
import { useShiftReportTemplate } from '@/hooks/useShiftReportTemplate';
import { DEFAULT_SHIFT_REPORT_TEMPLATE, getEnabledShiftReportSections, type ShiftReportTemplate } from '@/utils/shiftReportTemplate';

export type RangeKey = 'today' | 'week' | 'month';

export interface FieldTotal {
  fieldId: string;
  label: string;
  total: number;
}

const STAFF_INPUTS = new Set(['staff', 'staffMulti']);

const buildLabelMap = (template: ShiftReportTemplate): Record<string, string> => {
  const map: Record<string, string> = {};
  getEnabledShiftReportSections(template).forEach((section) => {
    section.fields.forEach((f) => {
      if (!f.enabled) return;
      if (STAFF_INPUTS.has(f.inputType ?? 'text')) return;
      map[f.id] = f.label;
    });
  });
  return map;
};

const rangeStart = (key: RangeKey): Date => {
  const now = new Date();
  if (key === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'week') return startOfWeek(now, { weekStartsOn: 1 });
  return startOfMonth(now);
};

export const useShiftReportTotals = (siteIds: string[]) => {
  const { data: template = DEFAULT_SHIFT_REPORT_TEMPLATE } = useShiftReportTemplate();
  const labelMap = buildLabelMap(template);

  const enabled = siteIds.length > 0;
  const monthStart = format(rangeStart('month'), 'yyyy-MM-dd');
  const today = format(endOfDay(new Date()), 'yyyy-MM-dd');

  const query = useQuery({
    queryKey: ['shift-report-totals', siteIds.slice().sort().join(','), monthStart, today],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('shift_report_entries')
        .select('site_id, report_date, values')
        .in('site_id', siteIds)
        .gte('report_date', monthStart)
        .lte('report_date', today);
      if (error) throw error;
      return data ?? [];
    },
  });

  const compute = (key: RangeKey): FieldTotal[] => {
    const start = format(rangeStart(key), 'yyyy-MM-dd');
    const totals: Record<string, number> = {};
    (query.data ?? []).forEach((row: any) => {
      if (row.report_date < start) return;
      const values = (row.values ?? {}) as Record<string, unknown>;
      Object.entries(values).forEach(([fieldId, raw]) => {
        if (!labelMap[fieldId]) return;
        const num = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').trim());
        if (!Number.isFinite(num) || num === 0) return;
        totals[fieldId] = (totals[fieldId] ?? 0) + num;
      });
    });
    return Object.entries(totals)
      .map(([fieldId, total]) => ({ fieldId, label: labelMap[fieldId], total }))
      .sort((a, b) => b.total - a.total);
  };

  return {
    isLoading: query.isLoading,
    today: compute('today'),
    week: compute('week'),
    month: compute('month'),
  };
};
